import { supabase } from './supabase';
import { registerCurrentDevice } from './devices';
import { clearAllCache } from './cache';
import { isNativeApp } from './nativeShell';

/**
 * Re-export the new context-backed useSession so every existing call site
 * keeps working without changes. The actual implementation, and the device-
 * revocation enforcement logic that used to live in this file, both live in
 * the provider — see components/SessionProvider.tsx for the why.
 */
export { useSession } from '../components/SessionProvider';

/** Reads a partner referral code from the current URL or localStorage.
 *  Persisted across navigations so a visitor landing on the marketing
 *  page can still credit the partner when they sign up later. */
export function getPartnerRefCode(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('ref');
    if (fromUrl) {
      window.localStorage.setItem('partner_ref', fromUrl);
      return fromUrl;
    }
    return window.localStorage.getItem('partner_ref');
  } catch {
    return null;
  }
}

/**
 * The plan/billing the visitor chose on the marketing pricing page before
 * they had an account. Persisted through signup + email verification so the
 * post-verify handoff can drop them straight into Stripe Checkout for the
 * right tier. See task #142 — marketing buttons used to skip signup and
 * send visitors direct to Stripe, leaving them with no account after
 * paying. Both the Signup form (localStorage) and this metadata field are
 * populated; user_metadata is the source of truth because it survives a
 * device switch during email verification.
 */
export type PendingCheckoutTier = 'tier1' | 'tier2' | 'tier3';
export type PendingCheckoutBilling = 'monthly' | 'annual';
export interface PendingCheckout {
  tier: PendingCheckoutTier;
  billing: PendingCheckoutBilling;
}

export async function signUpWithEmail(opts: {
  email: string;
  password: string;
  fullName: string;
  orgName: string;
  partnerRef?: string;
  pendingCheckout?: PendingCheckout;
}) {
  // The on_auth_user_created trigger creates the profiles row automatically.
  // We stash the org name + partner ref in user metadata; once the user is
  // authenticated (after email confirmation, if enabled), `ensureUserHasOrg`
  // reads the metadata and creates the organisation via the RPC.
  // Prefer an explicit code typed at signup; fall back to URL/localStorage.
  const partnerRef = (opts.partnerRef && opts.partnerRef.trim()) || getPartnerRefCode();
  const pendingCheckout = opts.pendingCheckout ?? null;
  // Where the account was created. Inside the phone app the confirmation
  // email link opens in Safari/Chrome, so we point it at /email-confirmed
  // which hands the customer back to the app (see pages/EmailConfirmed.tsx).
  const fromApp = isNativeApp();
  const { data, error } = await supabase.auth.signUp({
    email: opts.email,
    password: opts.password,
    options: {
      ...(fromApp
        ? { emailRedirectTo: 'https://app.safeandsorted.com/email-confirmed?source=app' }
        : {}),
      data: {
        signup_source: fromApp ? 'app' : 'web',
        full_name: opts.fullName,
        pending_org_name: opts.orgName,
        ...(partnerRef ? { pending_partner_ref: partnerRef } : {}),
        // pending_checkout survives even if the user verifies their email
        // on a different browser than they signed up in (localStorage is
        // per-device; user_metadata is per-user). Read by the post-verify
        // handoff in RequireSubscription to route them to /checkout instead
        // of /paywall.
        ...(pendingCheckout ? { pending_checkout: pendingCheckout } : {}),
      },
    },
  });
  if (error) throw error;
  // Supabase deliberately returns a 200 with an empty `identities` array
  // when the email is already registered, to avoid leaking which emails
  // exist (account enumeration). We surface that as "use Sign In" so the
  // user isn't left staring at a "check your email" message that will
  // never arrive. Investigated 11 Jun 2026 after a real customer hit it.
  if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
    const err = new Error('EMAIL_EXISTS');
    (err as Error & { code?: string }).code = 'email_exists';
    throw err;
  }
  // Clear the ref so a later sign-up on the same browser doesn't double-credit
  try { window.localStorage.removeItem('partner_ref'); } catch { /* noop */ }
  return data;
}

export async function signInWithEmail(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  // Register this device for the user. Soft-fails if the RPC isn't yet
  // deployed (e.g. before migration 014 has run).
  await registerCurrentDevice().catch(() => null);
  return data;
}

export async function signOut() {
  // Drop the offline cache BEFORE clearing Supabase auth — otherwise a
  // shared laptop could leak data to the next user who signs in here.
  // Soft-fails if IndexedDB is unavailable (private mode, etc).
  await clearAllCache().catch(() => null);
  // Also clear the localStorage org cache used by useUserOrg's
  // stale-while-revalidate path — same shared-device concern.
  try { localStorage.removeItem('hs:org-cache:v1'); } catch { /* noop */ }
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/**
 * Send a password reset email. Supabase emails the user a magic link that
 * lands on /reset-password where they pick a new password.
 *
 * The redirectTo URL must be in the project's Auth → URL Configuration
 * 'Redirect URLs' allowlist (we set this when wiring up Netlify earlier).
 */
export async function sendPasswordReset(email: string) {
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
}

/**
 * Set a new password for the currently-signed-in user. Used on the
 * /reset-password page after the user follows the magic link from the
 * password-reset email — Supabase signs them in automatically when the
 * link is opened, so this just updates the password on their session.
 */
export async function updatePassword(newPassword: string) {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}

/**
 * Make sure the currently authenticated user has at least one organisation
 * membership. If not, creates one from `user_metadata.pending_org_name`.
 * Idempotent — safe to call on every Dashboard mount.
 */
export async function ensureUserHasOrg(): Promise<{ orgId: string; created: boolean }> {
  const { data: userResult } = await supabase.auth.getUser();
  const user = userResult?.user;
  if (!user) throw new Error('Not authenticated');

  const { data: existing, error: existingErr } = await supabase
    .from('org_members')
    .select('org_id')
    .eq('user_id', user.id)
    .limit(1);
  if (existingErr) throw existingErr;
  if (existing && existing.length > 0) {
    return { orgId: existing[0].org_id as string, created: false };
  }

  const pending = user.user_metadata?.pending_org_name as string | undefined;
  const fallback = (user.email?.split('@')[0] ?? 'My') + ' organisation';
  const orgName = pending && pending.trim().length > 0 ? pending : fallback;

  const { data: newOrgId, error: rpcErr } = await supabase.rpc(
    'create_organisation_with_admin',
    { org_name: orgName }
  );
  if (rpcErr) throw rpcErr;
  return { orgId: newOrgId as string, created: true };
}

/**
 * localStorage key for the pending checkout stashed at signup time. Read by
 * the post-verify hook in RequireSubscription — if present, the user gets
 * routed to /checkout instead of /paywall.
 *
 * Cleared by clearPendingCheckout() BEFORE we redirect to Stripe, so the
 * browser back-button doesn't put the user in an infinite re-checkout
 * loop.
 */
const PENDING_CHECKOUT_KEY = 'hs:pending_checkout';

/** Coerces an untrusted value into a valid PendingCheckout, or null. */
function coercePendingCheckout(v: unknown): PendingCheckout | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as { tier?: unknown; billing?: unknown };
  const tier = o.tier;
  const billing = o.billing;
  if (tier !== 'tier1' && tier !== 'tier2' && tier !== 'tier3') return null;
  if (billing !== 'monthly' && billing !== 'annual') return null;
  return { tier, billing };
}

/**
 * Read the pending checkout for the currently-signed-in user, preferring
 * user_metadata (survives cross-device email verification) and falling back
 * to localStorage. Returns null if no valid pending checkout is stashed.
 */
export async function readPendingCheckout(): Promise<PendingCheckout | null> {
  // user_metadata first — it's the source of truth because it survives
  // the "signed up on laptop, verified email on phone" case.
  try {
    const { data } = await supabase.auth.getUser();
    const fromMeta = coercePendingCheckout(data.user?.user_metadata?.pending_checkout);
    if (fromMeta) return fromMeta;
  } catch { /* noop — fall through to localStorage */ }
  try {
    const raw = window.localStorage.getItem(PENDING_CHECKOUT_KEY);
    if (!raw) return null;
    return coercePendingCheckout(JSON.parse(raw));
  } catch { return null; }
}

/** Persist the pending checkout to localStorage (per-device). Called from
 *  Signup + Login so we've stashed it even before the signup completes. */
export function writePendingCheckoutLocal(pc: PendingCheckout): void {
  try {
    window.localStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(pc));
  } catch { /* quota / disabled — non-fatal, user_metadata is the backup */ }
}

/**
 * Clear the pending checkout from BOTH localStorage and user_metadata.
 * Called from the Checkout page BEFORE redirecting to Stripe so the
 * back-button doesn't retrigger the redirect. Errors are swallowed —
 * the redirect must proceed even if the metadata update round-trip
 * fails, otherwise we'd trap the customer here.
 */
export async function clearPendingCheckout(): Promise<void> {
  try { window.localStorage.removeItem(PENDING_CHECKOUT_KEY); } catch { /* noop */ }
  try {
    // Setting the key to null in user_metadata clears it; Supabase merges
    // the passed object into existing metadata.
    await supabase.auth.updateUser({ data: { pending_checkout: null } });
  } catch { /* noop — swallowed on purpose, see above */ }
}
